import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { useMarkPageVisited } from '../hooks/usePageVisit';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ClipboardList, 
  Search, 
  Eye,
  CheckCircle2,
  XCircle,
  Send,
  Download,
  Filter,
  Loader2,
  Plus,
  Trash2,
  CheckSquare,
  Square,
  Copy,
  Mail
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { formatErrorMessage } from '../utils/errorFormatter';

const OrdersPage = () => {
  const { api, isAdmin, isResponsable } = useAuth();
  const { isTrialExpired } = useTrialStatus();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Mark this page as visited
  useMarkPageVisited('/orders');
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [search, setSearch] = useState('');
  
  // Selection state for bulk delete
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Duplication state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [orderToDuplicate, setOrderToDuplicate] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  
  // Email selection state for sending order
  const [emailSelectionDialogOpen, setEmailSelectionDialogOpen] = useState(false);
  const [orderToSend, setOrderToSend] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
        const response = await api.get(`/orders${params}`);
        setOrders(response.data);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [api, statusFilter]);

  useEffect(() => {
    if (statusFilter !== 'all') {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, setSearchParams]);

  // Reset selection when exiting select mode
  useEffect(() => {
    if (!selectMode) {
      setSelectedOrders(new Set());
    }
  }, [selectMode]);

  const filteredOrders = orders.filter(order =>
    order.order_number.toLowerCase().includes(search.toLowerCase()) ||
    order.supplier_name.toLowerCase().includes(search.toLowerCase()) ||
    order.user_name.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      validated: 'bg-blue-100 text-blue-800 border-blue-200',
      sent: 'bg-green-100 text-green-800 border-green-200',
      delivered: 'bg-[#D8F3DC] text-[#1B4332] border-[#1B4332]/20',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    const labels = {
      pending: 'En attente',
      validated: 'Validée',
      sent: 'Envoyée',
      delivered: 'Livrée',
      rejected: 'Rejetée'
    };
    return (
      <Badge variant="outline" className={styles[status]}>
        {labels[status]}
      </Badge>
    );
  };

  const handleValidate = async (orderId, e) => {
    e.stopPropagation();
    try {
      await api.put(`/orders/${orderId}/validate`);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: 'validated' } : o));
      toast.success('Commande validée');
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleReject = async (orderId, e) => {
    e.stopPropagation();
    try {
      await api.put(`/orders/${orderId}/reject`);
      setOrders(orders.map(o => o.id === orderId ? { ...o, status: 'rejected' } : o));
      toast.success('Commande rejetée');
    } catch (error) {
      toast.error('Erreur lors du rejet');
    }
  };

  const handleSend = async (orderId, e) => {
    e.stopPropagation();
    
    // Find the order and its supplier to get available emails
    const order = orders.find(o => o.id === orderId);
    if (!order || !order.supplier_id) {
      toast.error('Commande ou fournisseur introuvable');
      return;
    }
    
    // Fetch supplier to get all emails
    try {
      const response = await api.get(`/suppliers/${order.supplier_id}`);
      const supplier = response.data;
      
      // Build list of available emails
      const availableEmails = [supplier.email];
      if (supplier.secondary_emails && supplier.secondary_emails.length > 0) {
        availableEmails.push(...supplier.secondary_emails);
      }
      
      setOrderToSend({ id: orderId, supplier, availableEmails });
      setSelectedEmails([supplier.email]); // Pre-select primary email
      setEmailSelectionDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors de la récupération du fournisseur');
    }
  };
  
  const confirmSendOrder = async () => {
    if (selectedEmails.length === 0) {
      toast.error('Veuillez sélectionner au moins un email');
      return;
    }
    
    setSending(true);
    try {
      await api.put(`/orders/${orderToSend.id}/send`, {
        recipient_emails: selectedEmails
      });
      setOrders(orders.map(o => o.id === orderToSend.id ? { ...o, status: 'sent' } : o));
      toast.success(`Commande envoyée à ${selectedEmails.length} email(s)`);
      setEmailSelectionDialogOpen(false);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de l\'envoi'));
    } finally {
      setSending(false);
    }
  };
  
  const toggleEmailSelection = (email) => {
    setSelectedEmails(prev => 
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const handleDownloadPDF = async (orderId, orderNumber, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/orders/${orderId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDuplicateClick = (order, e) => {
    e.stopPropagation();
    setOrderToDuplicate(order);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateConfirm = async () => {
    if (!orderToDuplicate) return;
    
    setDuplicating(true);
    try {
      const response = await api.post(`/orders/${orderToDuplicate.id}/duplicate`);
      const newOrderId = response.data.id;
      
      toast.success('Commande dupliquée', {
        description: `Nouvelle commande ${response.data.order_number}`,
        action: {
          label: 'Ouvrir',
          onClick: () => navigate(`/orders/${newOrderId}`)
        }
      });
      
      // Refresh orders list
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const ordersResponse = await api.get(`/orders${params}`);
      setOrders(ordersResponse.data);
      
      setDuplicateDialogOpen(false);
      setOrderToDuplicate(null);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Erreur lors de la duplication'));
    } finally {
      setDuplicating(false);
    }
  };

  // Selection handlers
  const toggleOrderSelection = (orderId, e) => {
    e.stopPropagation();
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const selectAllOrders = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(o => o.id)));
    }
  };

  const handleBulkDelete = async () => {
    setDeleting(true);
    try {
      let deleted = 0;
      let errors = 0;
      
      for (const orderId of selectedOrders) {
        try {
          await api.delete(`/orders/${orderId}/admin`);
          deleted++;
        } catch (error) {
          errors++;
        }
      }
      
      // Refresh orders list
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/orders${params}`);
      setOrders(response.data);
      
      setSelectedOrders(new Set());
      setSelectMode(false);
      setDeleteDialogOpen(false);
      
      if (errors === 0) {
        toast.success(`${deleted} commande(s) supprimée(s)`);
      } else {
        toast.warning(`${deleted} supprimée(s), ${errors} erreur(s)`);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4332]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="orders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Commandes</h1>
          <p className="text-gray-500">{orders.length} commandes au total</p>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Suivez vos commandes depuis l'envoi jusqu'à la réception. Consultez l'historique et gérez les commandes en attente.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Admin: Toggle select mode */}
          {isAdmin && !selectMode && (
            <Button 
              variant="outline"
              onClick={() => setSelectMode(true)}
              className="border-red-200 text-red-600 hover:bg-red-50"
              data-testid="select-mode-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Gérer
            </Button>
          )}
          
          {/* Select mode actions */}
          {selectMode && (
            <>
              <Button 
                variant="outline"
                onClick={() => setSelectMode(false)}
              >
                Annuler
              </Button>
              <Button 
                variant="outline"
                onClick={selectAllOrders}
                className="border-gray-300"
              >
                {selectedOrders.size === filteredOrders.length ? (
                  <><CheckSquare className="w-4 h-4 mr-2" /> Tout désélectionner</>
                ) : (
                  <><Square className="w-4 h-4 mr-2" /> Tout sélectionner</>
                )}
              </Button>
              <Button 
                variant="destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={selectedOrders.size === 0}
                data-testid="delete-selected-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer ({selectedOrders.size})
              </Button>
            </>
          )}
          
          {!selectMode && (
            <Button 
              onClick={() => navigate('/catalog')}
              disabled={isTrialExpired}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle commande
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Rechercher une commande..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12"
            data-testid="orders-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px] h-12" data-testid="status-filter-select">
            <Filter className="w-4 h-4 mr-2 text-gray-400" />
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="validated">Validées</SelectItem>
            <SelectItem value="sent">Envoyées</SelectItem>
            <SelectItem value="delivered">Livrées</SelectItem>
            <SelectItem value="rejected">Rejetées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredOrders.length === 0 ? (
        <Card className="border border-gray-200">
          <CardContent className="py-12 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">Aucune commande</h3>
            <p className="text-gray-500 mb-4">Commencez par créer une nouvelle commande</p>
            <Button 
              onClick={() => navigate('/catalog')}
              className="bg-[#1B4332] hover:bg-[#0D2E1F] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Commander
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-gray-200">
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {filteredOrders.map((order) => (
                <div
                  key={order.id}
                  className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                    selectedOrders.has(order.id) ? 'bg-red-50' : ''
                  }`}
                  onClick={() => selectMode ? toggleOrderSelection(order.id, { stopPropagation: () => {} }) : navigate(`/orders/${order.id}`)}
                  data-testid={`order-row-${order.order_number}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Checkbox for selection */}
                    {selectMode && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedOrders.has(order.id)}
                          onCheckedChange={() => toggleOrderSelection(order.id, { stopPropagation: () => {} })}
                          className="data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                        />
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-semibold text-gray-900">{order.order_number}</span>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span>{order.supplier_name}</span>
                        <span>•</span>
                        <span>{order.user_name}</span>
                        <span>•</span>
                        <span>Livraison: {format(new Date(order.delivery_date), 'dd/MM/yyyy')}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-lg tabular-nums hidden sm:block">
                        {order.total_amount.toFixed(2)} €
                      </span>

                      {!selectMode && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9"
                            onClick={(e) => handleDuplicateClick(order, e)}
                            title="Dupliquer"
                            data-testid="order-row-duplicate-button"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          
                          {(isAdmin || isResponsable) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9"
                              onClick={(e) => handleDownloadPDF(order.id, order.order_number, e)}
                              title="Télécharger PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}

                          {(isAdmin || isResponsable) && order.status === 'pending' && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => handleValidate(order.id, e)}
                                title="Valider"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={(e) => handleReject(order.id, e)}
                                title="Rejeter"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}

                          {isAdmin && order.status === 'validated' && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-[#1B4332] hover:text-[#0D2E1F] hover:bg-[#D8F3DC]"
                              onClick={(e) => handleSend(order.id, e)}
                              title="Envoyer au fournisseur"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}

                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9"
                            onClick={() => navigate(`/orders/${order.id}`)}
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer les commandes ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous êtes sur le point de supprimer <strong>{selectedOrders.size} commande(s)</strong>.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate confirmation dialog */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Dupliquer la commande ?</AlertDialogTitle>
            <AlertDialogDescription>
              Crée une nouvelle commande avec les mêmes produits et quantités que <strong>{orderToDuplicate?.order_number}</strong>.
              Vous pourrez modifier avant validation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={duplicating} data-testid="order-duplicate-cancel-button">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDuplicateConfirm}
              disabled={duplicating}
              className="bg-[#1B4332] hover:bg-[#0D2E1F]"
              data-testid="order-duplicate-confirm-button"
            >
              {duplicating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Duplication...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Dupliquer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Selection Dialog for Sending Order */}
      <Dialog open={emailSelectionDialogOpen} onOpenChange={setEmailSelectionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1B4332]" />
              Sélectionner les destinataires
            </DialogTitle>
            <DialogDescription>
              Choisissez les emails qui recevront cette commande pour{' '}
              <strong>{orderToSend?.supplier?.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {orderToSend?.availableEmails?.map((email, index) => (
              <div
                key={email}
                onClick={() => toggleEmailSelection(email)}
                className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <Checkbox
                  checked={selectedEmails.includes(email)}
                  onCheckedChange={() => toggleEmailSelection(email)}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{email}</span>
                  </div>
                  {index === 0 && (
                    <Badge className="mt-1 text-xs bg-[#1B4332]">Principal</Badge>
                  )}
                  {index > 0 && (
                    <span className="text-xs text-gray-500">Email secondaire {index}</span>
                  )}
                </div>
              </div>
            ))}
            
            {selectedEmails.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <strong>{selectedEmails.length}</strong> email(s) sélectionné(s)
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEmailSelectionDialogOpen(false)}
              disabled={sending}
            >
              Annuler
            </Button>
            <Button
              onClick={confirmSendOrder}
              disabled={sending || selectedEmails.length === 0}
              className="bg-[#1B4332] hover:bg-[#0D2E1F]"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer la commande
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersPage;
